import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { EnterpriseSettingsService, MfaPolicyDto, MfaPolicyService } from 'lib';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, switchMap } from 'rxjs';

@Component({
    selector: 'mmoga-settings',
    templateUrl: './settings.component.html',
    standalone: true,
    imports: [
        ReactiveFormsModule
    ],
    styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit {
    private router = inject(Router);
    private fb = inject(FormBuilder);
    private enterpriseSettingsService = inject(EnterpriseSettingsService);
    private mfaPolicyService = inject(MfaPolicyService);
    private activatedRoute = inject(ActivatedRoute);
    private readonly toastrService = inject(ToastrService);

    settingsForm = this.fb.group({
        isHeapEnabled: [false],
        isMfaEnabled: [true]
    });
    enterpriseId = 0;
    isSaving = false;
    isLoaded = false;
    private currentMfaPolicy?: MfaPolicyDto;

    ngOnInit(): void {
        this.fetchSettings();
    }

    save() {
        if (this.isSaving) {
            return;
        }
        this.isSaving = true;
        this.saveSettings();
    }

    cancel() {
        this.router.navigate(['/home/groups']);
    }

    public fetchSettings() {
        this.activatedRoute.parent?.params
            .pipe(
                switchMap(params => {
                    this.enterpriseId = +params[ 'id' ] || 0;
                    return forkJoin({
                        settings: this.enterpriseSettingsService.getEnterpriseSettingsPolicy(this.enterpriseId),
                        mfaPolicy: this.mfaPolicyService.getMfaPolicy(this.enterpriseId)
                    });
                })
            )
            .subscribe(({ settings, mfaPolicy }) => {
                this.currentMfaPolicy = mfaPolicy;
                this.settingsForm.patchValue({
                    isHeapEnabled: settings.heapAnalyticsEnabled,
                    isMfaEnabled: mfaPolicy.staffEnabled
                });
                this.isLoaded = true;
            });
    }

    public saveSettings() {
        forkJoin({
            settings: this.enterpriseSettingsService.saveEnterpriseSettingsPolicy(this.enterpriseId, {
                heapAnalyticsEnabled: !!this.settingsForm.value.isHeapEnabled
            }),
            mfaPolicy: this.mfaPolicyService.saveMfaPolicy(this.enterpriseId, {
                ...this.currentMfaPolicy as MfaPolicyDto,
                staffEnabled: !!this.settingsForm.get('isMfaEnabled')?.value
            })
        })
            .subscribe({
                next: () => {
                    this.toastrService.success('Enterprise Settings Updated', 'Settings');
                    this.isSaving = false;
                },
                error: (error) => {
                    const errorMessage = error?.error?.message || 'There was an error when trying to update Enterprise Settings.';
                    this.toastrService.error(errorMessage, 'Settings');
                    this.isSaving = false;
                }
            });
    }

}
